import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { selectProviders } from 'store/system/selectors';

import useFormContext from '../hooks/useFormContext';
import LoanActionAiProcessSelect from '../LoanActionAiProcessSelect';
import {
  getOptionName,
  getSelectedOption,
  isSameOption,
} from '../LoanActionAiProcessSelect/LoanActionAiProcessSelect.utils';
import { wrapperClassname } from '../LoanActionItem.constants';
import LoanActionTagSelect from '../LoanActionTagSelect';

import {
  NO_DELIVERY_CONNECTOR_TEXT,
  SEND_AI_PROCESS_RESULTS_TAG_FIELDS,
  SEND_AI_PROCESS_RESULTS_TAGS_TITLE,
} from './LoanActionSendAiProcessResultsRow.constants';
import { LoanActionSendAiProcessResultsRowTestIds } from './LoanActionSendAiProcessResultsRow.testIds';
import { getDeliveryConnectorOptions } from './LoanActionSendAiProcessResultsRow.utils';

const LoanActionSendAiProcessResultsRow = ({
  actionIndex,
  rootPath,
  tagsOptions,
  isDisabled,
  isEditAllowed,
}) => {
  const { form, validationErrors, setInForm, validateField } = useFormContext();
  const providers = useSelector(selectProviders);

  const sendAiProcessResults = _.get(form, rootPath) ?? {};
  const { processId = null, providerKey = null } = sendAiProcessResults;
  const processIdError = _.get(validationErrors, `${rootPath}.processId`) ?? null;
  const providerKeyError = _.get(validationErrors, `${rootPath}.providerKey`) ?? null;
  const isFieldDisabled = isDisabled || !isEditAllowed;

  const connectorOptions = useMemo(
    () => getDeliveryConnectorOptions({ providers, providerKey }),
    [providers, providerKey],
  );
  const selectedConnector = getSelectedOption({ options: connectorOptions, value: providerKey });

  const onFieldChange = useCallback(
    (fieldPath, value) => {
      setInForm(fieldPath, value);
      validateField?.(fieldPath, value);
    },
    [setInForm, validateField],
  );

  const onProcessChange = useCallback(
    nextProcessId => onFieldChange(`${rootPath}.processId`, nextProcessId),
    [onFieldChange, rootPath],
  );

  const onConnectorChange = useCallback(
    (__, option) => onFieldChange(`${rootPath}.providerKey`, option?.value ?? null),
    [onFieldChange, rootPath],
  );

  return (
    <Box className={wrapperClassname}>
      <LoanActionAiProcessSelect
        dataTestId={LoanActionSendAiProcessResultsRowTestIds.getProcessSelectId(actionIndex)}
        error={processIdError}
        isDisabled={isFieldDisabled}
        name={`${rootPath}.processId`}
        processId={processId}
        onChange={onProcessChange}
      />
      <AutocompleteCDS
        disableClearable
        required
        dataTestId={LoanActionSendAiProcessResultsRowTestIds.getConnectorSelectId(actionIndex)}
        disabled={isFieldDisabled}
        error={!!providerKeyError}
        getOptionLabel={getOptionName}
        helperText={providerKeyError ?? ''}
        isOptionEqualToValue={isSameOption}
        label="Connector"
        name={`${rootPath}.providerKey`}
        noOptionsText={NO_DELIVERY_CONNECTOR_TEXT}
        options={connectorOptions}
        value={selectedConnector}
        onChange={onConnectorChange}
      />
      <Typography color="text.secondary" variant="medium14">
        {SEND_AI_PROCESS_RESULTS_TAGS_TITLE}
      </Typography>
      {SEND_AI_PROCESS_RESULTS_TAG_FIELDS.map(({ key, testIdKey, label }) => (
        <LoanActionTagSelect
          dataTestId={LoanActionSendAiProcessResultsRowTestIds.getTagSelectId(
            actionIndex,
            testIdKey,
          )}
          isDisabled={isFieldDisabled}
          key={key}
          label={label}
          name={`${rootPath}.${key}`}
          tagId={sendAiProcessResults[key] ?? null}
          tagsOptions={tagsOptions}
          onChange={setInForm}
        />
      ))}
    </Box>
  );
};

LoanActionSendAiProcessResultsRow.propTypes = {
  actionIndex: PropTypes.number.isRequired,
  rootPath: PropTypes.string.isRequired,
  tagsOptions: PropTypes.array.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
};

export default memo(LoanActionSendAiProcessResultsRow);
